import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { formatINR, formatDate } from "../../lib/format";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { LogOut, Check, X, Download, Plus, Trash2, ShieldCheck, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, sub }) {
  return (
    <Card className="rounded-2xl border-border shadow-card-sm p-5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-heading text-2xl font-medium text-ink mt-1 tnum">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

const FB_TYPES = { confusing: "Confusing", calculation: "Calculation", detection: "Detection", feature: "Feature request", bug: "Not working", general: "General" };
const FB_STATUS = ["new", "reviewing", "planned", "resolved"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [contactMsgs, setContactMsgs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loginCodes, setLoginCodes] = useState([]);
  const [revealed, setRevealed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteDraft, setNoteDraft] = useState({});

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, ta, u, f, c, lc, cm] = await Promise.all([
        api.get("/admin/analytics"), api.get("/admin/trial-applications"),
        api.get("/admin/users"), api.get("/admin/feedback"), api.get("/admin/invitation-codes"),
        api.get("/admin/login-codes"), api.get("/admin/contact-messages"),
      ]);
      setAnalytics(a.data); setApps(ta.data); setUsers(u.data); setFeedback(f.data); setCodes(c.data); setLoginCodes(lc.data); setContactMsgs(cm.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const reviewApp = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/trial-applications/${id}`, { status });
      if (status === "approved" && data?.welcome) {
        const w = data.welcome;
        if (w.code) {
          setRevealed({ email: w.email, code: w.code, expires_at: w.expires_at, emailed: w.delivered });
          setTab("users");
        }
        if (w.delivered) toast.success(`Approved — welcome email with login code sent to ${w.email}.`);
        else toast.warning(`Approved. Email couldn't be delivered to ${w.email} — copy the login code below and share it directly.`);
      } else {
        toast.success(`Request ${status}.`);
      }
      loadAll();
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleUser = async (u) => {
    const status = u.status === "suspended" ? "active" : "suspended";
    try { await api.patch(`/admin/users/${u.id}/status`, { status }); toast.success(`User ${status}.`); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const resetUserPw = async (u) => {
    const pw = window.prompt(`Set a new temporary password for ${u.email} (min 8 chars). Share it securely with the user.`);
    if (!pw) return;
    try { await api.post(`/admin/users/${u.id}/reset-password`, { new_password: pw }); toast.success("Password reset. Share it securely with the user."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const genLoginCode = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/login-code`);
      setRevealed({ email: u.email, code: data.code, expires_at: data.expires_at });
      toast.success("Login code generated. Copy it now — it won't be shown again.");
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Code copied to clipboard."); }
    catch { toast.error("Couldn't copy — select and copy manually."); }
  };
  const revokeLoginCode = async (id) => {
    try { await api.delete(`/admin/login-codes/${id}`); toast.success("Code revoked."); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const updateFb = async (id, body) => {
    try { await api.patch(`/admin/feedback/${id}`, body); toast.success("Updated."); loadAll(); }    catch (e) { toast.error(apiErr(e)); }
  };
  const updateContact = async (id, status) => {
    try { await api.patch(`/admin/contact-messages/${id}`, { status }); toast.success("Updated."); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createCode = async () => {
    try { await api.post("/admin/invitation-codes", { max_uses: 1 }); toast.success("Code created."); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delCode = async (id) => { try { await api.delete(`/admin/invitation-codes/${id}`); loadAll(); } catch (e) { toast.error(apiErr(e)); } };
  const exportCsv = () => { window.open(`${API}/admin/feedback/export`, "_blank"); };

  const doLogout = async () => { await logout(); nav("/"); };
  const pending = apps.filter((a) => a.status === "pending");
  const newMsgs = contactMsgs.filter((m) => m.status === "new");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white/70 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo size={32} /><Badge className="bg-navy text-white rounded-full"><ShieldCheck size={12} className="mr-1" />Founder</Badge></div>
          <Button variant="ghost" onClick={doLogout} className="rounded-full text-slatey" data-testid="admin-logout"><LogOut size={16} className="mr-1.5" />Sign out</Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        <h1 className="font-heading text-2xl font-medium text-ink mb-1">Founder dashboard</h1>
        <p className="text-sm text-muted-foreground mb-6">Welcome, {user?.name}. Manage the private trial.</p>

        {loading ? <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4,5,6,7,8].map(i=><Skeleton key={i} className="h-24 rounded-2xl" />)}</div> : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-full bg-secondary flex w-full md:w-auto overflow-x-auto no-scrollbar mb-6">
              <TabsTrigger value="overview" className="rounded-full" data-testid="admin-tab-overview">Analytics</TabsTrigger>
              <TabsTrigger value="requests" className="rounded-full" data-testid="admin-tab-requests">Requests {pending.length > 0 && <span className="ml-1.5 h-5 w-5 rounded-full bg-mint text-white text-[10px] flex items-center justify-center">{pending.length}</span>}</TabsTrigger>
              <TabsTrigger value="users" className="rounded-full" data-testid="admin-tab-users">Users</TabsTrigger>
              <TabsTrigger value="feedback" className="rounded-full" data-testid="admin-tab-feedback">Feedback</TabsTrigger>
              <TabsTrigger value="messages" className="rounded-full" data-testid="admin-tab-messages">Messages {newMsgs.length > 0 && <span className="ml-1.5 h-5 w-5 rounded-full bg-mint text-white text-[10px] flex items-center justify-center">{newMsgs.length}</span>}</TabsTrigger>
              <TabsTrigger value="invites" className="rounded-full" data-testid="admin-tab-invites">Invites</TabsTrigger>
            </TabsList>

            {/* Analytics */}
            <TabsContent value="overview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Registered trial users" value={analytics.total_registered} />
                <Stat label="Approved requests" value={analytics.approved_trial_users} sub={`${analytics.pending_requests} pending`} />
                <Stat label="Onboarding completion" value={`${analytics.onboarding_completion_rate}%`} />
                <Stat label="Added ≥1 commitment" value={analytics.users_with_commitment} />
                <Stat label="Avg commitments/user" value={analytics.avg_commitments_per_user} />
                <Stat label="Manual vs AI" value={`${analytics.manual_entries} / ${analytics.ai_assisted_entries}`} sub="entries" />
                <Stat label="AI correction rate" value={`${analytics.ai_correction_rate}%`} />
                <Stat label="Dashboard return rate" value={`${analytics.dashboard_return_rate}%`} />
                <Stat label="Cancelled / paused" value={analytics.cancelled_or_paused} />
                <Stat label="Self-reported savings" value={formatINR(analytics.self_reported_savings)} sub="monthly equiv" />
                <Stat label="Feedback volume" value={analytics.feedback_volume} />
                <Stat label="Would pay ₹999/yr" value={`${analytics.willingness_to_pay.yes} yes`} sub={`${analytics.willingness_to_pay.maybe} maybe · ${analytics.willingness_to_pay.no} no`} />
              </div>
              <Card className="rounded-2xl border-border shadow-card-sm p-5 mt-4">
                <p className="text-sm font-medium text-ink mb-3">Product-market fit signal</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-calm-successbg p-4 text-center"><p className="font-heading text-2xl text-ink tnum">{analytics.pmf.very}</p><p className="text-xs text-slatey mt-1">Very disappointed</p></div>
                  <div className="rounded-xl bg-secondary p-4 text-center"><p className="font-heading text-2xl text-ink tnum">{analytics.pmf.somewhat}</p><p className="text-xs text-slatey mt-1">Somewhat</p></div>
                  <div className="rounded-xl bg-secondary p-4 text-center"><p className="font-heading text-2xl text-ink tnum">{analytics.pmf.not}</p><p className="text-xs text-slatey mt-1">Not disappointed</p></div>
                </div>
                <p className="text-sm font-medium text-ink mt-5 mb-2">Most-used features</p>
                <div className="flex flex-wrap gap-2">
                  {analytics.most_used_features.map((f) => <span key={f.feature} className="text-xs px-3 py-1.5 rounded-full bg-secondary text-slatey">{f.feature.replace(/_/g, " ")} · {f.count}</span>)}
                </div>
              </Card>
            </TabsContent>

            {/* Requests */}
            <TabsContent value="requests">
              <div className="space-y-3">
                {apps.length === 0 && <Card className="rounded-2xl border-border shadow-card-sm p-8 text-center text-muted-foreground">No trial requests yet.</Card>}
                {apps.map((a) => (
                  <Card key={a.id} className="rounded-2xl border-border shadow-card-sm p-5" data-testid={`trial-app-${a.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-ink">{a.full_name}</p>
                          <Badge className={`rounded-full text-[10px] ${a.status === "approved" ? "bg-calm-successbg text-calm-success" : a.status === "rejected" ? "bg-calm-alertbg text-calm-alert" : "bg-calm-pendingbg text-calm-pending"}`}>{a.status}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{a.email} · {a.mobile}</p>
                        <p className="text-xs text-slatey mt-1">{a.age_range} · {a.employment_type} · {a.num_commitments} commitments</p>
                        <p className="text-xs text-slatey mt-1 max-w-lg">“{a.reason}”</p>
                        <p className="text-[11px] text-muted-foreground mt-1">Requested {formatDate(a.created_at)}</p>
                      </div>
                      {a.status === "pending" && (
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" onClick={() => reviewApp(a.id, "approved")} className="rounded-full bg-mint text-white hover:bg-mint/90" data-testid={`approve-${a.id}`}><Check size={15} className="mr-1" />Approve</Button>
                          <Button size="sm" variant="secondary" onClick={() => reviewApp(a.id, "rejected")} className="rounded-full bg-secondary text-slatey" data-testid={`reject-${a.id}`}><X size={15} className="mr-1" />Reject</Button>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Users */}
            <TabsContent value="users">
              <div className="space-y-3">
                {users.map((u) => (
                  <Card key={u.id} className="rounded-2xl border-border shadow-card-sm p-4 flex items-center justify-between gap-3" data-testid={`admin-user-${u.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-ink truncate">{u.name}</p>
                        {u.role === "admin" && <Badge className="rounded-full bg-navy text-white text-[10px]">admin</Badge>}
                        {u.status === "suspended" && <Badge className="rounded-full bg-calm-alertbg text-calm-alert text-[10px]">suspended</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                      <p className="text-xs text-slatey mt-0.5">{u.commitment_count} commitments · {u.onboarding_complete ? "onboarded" : "not onboarded"} · last active {formatDate(u.last_active)}</p>
                    </div>
                    {u.role !== "admin" && (
                      <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                        <Button size="sm" variant="secondary" onClick={() => genLoginCode(u)} className="rounded-full bg-mint-light text-mint" data-testid={`gen-code-${u.id}`}>Login code</Button>
                        <Button size="sm" variant="secondary" onClick={() => resetUserPw(u)} className="rounded-full bg-secondary text-slatey" data-testid={`reset-pw-${u.id}`}>Reset password</Button>
                        <Button size="sm" variant="secondary" onClick={() => toggleUser(u)} className="rounded-full bg-secondary text-slatey" data-testid={`toggle-user-${u.id}`}>
                          {u.status === "suspended" ? "Reactivate" : "Suspend"}
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              {/* One-time reveal card */}
              {revealed && (
                <Card className="rounded-2xl border-mint/40 bg-mint-light/50 shadow-card-sm p-5 mt-5" data-testid="login-code-reveal">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-slatey flex items-center gap-1.5"><KeyRound size={13} className="text-mint" />Login code for <span className="font-medium text-ink">{revealed.email}</span></p>
                      <p className="font-mono font-semibold text-ink text-2xl tracking-wider mt-2 break-all" data-testid="revealed-code">{revealed.code}</p>
                      <p className="text-[11px] text-slatey mt-2">Single-use · expires {formatDate(revealed.expires_at)}. Shown once — only a hash is stored, so it can't be revealed again.</p>
                      {revealed.emailed === false && <p className="text-[11px] text-calm-alert mt-1" data-testid="code-manual-share">Email couldn't be delivered — copy this code and send it to the applicant directly.</p>}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button size="sm" onClick={() => copyCode(revealed.code)} className="rounded-full bg-navy text-white hover:bg-navy-light" data-testid="copy-revealed-code"><Copy size={14} className="mr-1.5" />Copy</Button>
                      <Button size="sm" variant="ghost" onClick={() => setRevealed(null)} className="rounded-full text-slatey" data-testid="dismiss-revealed-code">Dismiss</Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Login codes panel */}
              <div className="mt-6">
                <p className="text-sm font-medium text-ink mb-3 flex items-center gap-1.5"><KeyRound size={15} className="text-slatey" />Login codes</p>
                <div className="space-y-2">
                  {loginCodes.length === 0 && <Card className="rounded-2xl border-border shadow-card-sm p-6 text-center text-sm text-muted-foreground">No login codes generated yet. Use "Login code" on a user above to create one.</Card>}
                  {loginCodes.map((c) => (
                    <Card key={c.id} className="rounded-2xl border-border shadow-card-sm p-4 flex items-center justify-between gap-3" data-testid={`login-code-${c.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-ink truncate">{c.user_email}</p>
                          <Badge className={`rounded-full text-[10px] ${c.status === "active" ? "bg-calm-successbg text-calm-success" : c.status === "used" ? "bg-secondary text-slatey" : "bg-calm-alertbg text-calm-alert"}`} data-testid={`login-code-status-${c.id}`}>{c.status}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">created {formatDate(c.created_at)} · expires {formatDate(c.expires_at)}</p>
                      </div>
                      {c.status === "active" && (
                        <Button size="sm" variant="ghost" onClick={() => revokeLoginCode(c.id)} className="rounded-full text-calm-alert shrink-0" data-testid={`revoke-login-code-${c.id}`}><Trash2 size={15} className="mr-1" />Revoke</Button>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="feedback">
              <div className="flex justify-end mb-3">
                <Button size="sm" variant="secondary" onClick={exportCsv} className="rounded-full bg-secondary text-ink" data-testid="feedback-export"><Download size={15} className="mr-1.5" />Export CSV</Button>
              </div>
              <div className="space-y-3">
                {feedback.length === 0 && <Card className="rounded-2xl border-border shadow-card-sm p-8 text-center text-muted-foreground">No feedback yet.</Card>}
                {feedback.map((f) => (
                  <Card key={f.id} className="rounded-2xl border-border shadow-card-sm p-5" data-testid={`feedback-item-${f.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Badge className="rounded-full bg-secondary text-slatey text-[10px]">{FB_TYPES[f.type] || f.type}</Badge>
                        {f.rating && <span className="text-xs text-mint">★ {f.rating}</span>}
                        <span className="text-xs text-muted-foreground">{f.page}</span>
                      </div>
                      <Select value={f.status} onValueChange={(v) => updateFb(f.id, { status: v })}>
                        <SelectTrigger className="rounded-full h-8 w-32 text-xs" data-testid={`fb-status-${f.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{FB_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <p className="text-sm text-ink mt-3">{f.message}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{f.user_email} · {formatDate(f.created_at)}</p>
                    {f.admin_notes?.length > 0 && (
                      <div className="mt-3 space-y-1">{f.admin_notes.map((n, i) => <p key={i} className="text-xs text-slatey bg-secondary/60 rounded-lg px-3 py-1.5">📝 {n.text}</p>)}</div>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Textarea value={noteDraft[f.id] || ""} onChange={(e) => setNoteDraft((d) => ({ ...d, [f.id]: e.target.value }))} rows={1} placeholder="Add a private note…" className="rounded-xl text-sm" data-testid={`fb-note-${f.id}`} />
                      <Button size="sm" onClick={() => { updateFb(f.id, { note: noteDraft[f.id] }); setNoteDraft((d) => ({ ...d, [f.id]: "" })); }} className="rounded-full bg-navy text-white shrink-0">Add</Button>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Messages (Contact submissions) */}
            <TabsContent value="messages">
              <div className="space-y-3">
                {contactMsgs.length === 0 && <Card className="rounded-2xl border-border shadow-card-sm p-8 text-center text-muted-foreground" data-testid="messages-empty">No contact messages yet.</Card>}
                {contactMsgs.map((m) => (
                  <Card key={m.id} className="rounded-2xl border-border shadow-card-sm p-5" data-testid={`message-item-${m.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Badge className="rounded-full bg-mint-light text-mint text-[10px] capitalize">{m.category}</Badge>
                        <span className="text-sm font-medium text-ink">{m.name}</span>
                      </div>
                      <Select value={m.status || "new"} onValueChange={(v) => updateContact(m.id, v)}>
                        <SelectTrigger className="rounded-full h-8 w-32 text-xs" data-testid={`msg-status-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{["new", "reviewing", "resolved"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <a href={`mailto:${m.email}`} className="text-xs text-mint hover:underline mt-1 inline-block">{m.email}</a>
                    <p className="text-sm text-ink mt-2 whitespace-pre-wrap">{m.message}</p>
                    <p className="text-[11px] text-muted-foreground mt-1.5">{formatDate(m.created_at)}</p>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Invites */}
            <TabsContent value="invites">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-slatey">Invitation codes let users register without waiting for approval.</p>
                <Button size="sm" onClick={createCode} className="rounded-full bg-navy text-white hover:bg-navy-light" data-testid="create-code"><Plus size={15} className="mr-1" />New code</Button>
              </div>
              <div className="space-y-2">
                {codes.length === 0 && <Card className="rounded-2xl border-border shadow-card-sm p-8 text-center text-muted-foreground">No invitation codes yet.</Card>}
                {codes.map((c) => (
                  <Card key={c.id} className="rounded-2xl border-border shadow-card-sm p-4 flex items-center justify-between" data-testid={`code-${c.id}`}>
                    <div><p className="font-mono font-semibold text-ink text-lg">{c.code}</p><p className="text-xs text-muted-foreground">{c.uses}/{c.max_uses} used · created {formatDate(c.created_at)}</p></div>
                    <Button size="sm" variant="ghost" onClick={() => delCode(c.id)} className="rounded-full text-calm-alert"><Trash2 size={16} /></Button>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
